import PDQ from "../../constants/pdq";
import { tryCatch } from "../../helpers/middleware";
import {
    coverflowSwiperOptions,
    alternateVerticalSwiperOptions,
} from "../lux-swiper/options";
import { activateClasses } from "../../helpers/dom-manipulation";
import { CountdownTimer, getParsedValue } from "../../helpers/time";
import createPdqApi from "./api";

export class LuxPDQ {
    constructor(
        swiperIdHorizontal,
        swiperIdVertical,
        count,
        csrf,
        hasIntroductoryImage,
    ) {
        // Initialize properties
        this.api = createPdqApi(csrf);
        this.swiperIdHorizontal = swiperIdHorizontal;

        this.swiperIdVertical = [...Array(count).keys()].map(
            (i) => swiperIdVertical + (i + 1),
        );

        this.pdqSliderHorizontal = null;
        this.pdqSliderVertical = null;
        this.translate = 0;

        // DOM elements
        const modal = document.querySelector(PDQ.modal);
        const modalId = document.getElementById(PDQ.modalId);
        const modalButton = document.querySelector(PDQ.modalButton);
        const swiperWrapper = document.querySelector(PDQ.swiperWrapper);
        this.quickBuyButton = document.querySelector(PDQ.quickBuyButton);
        this.countdownDaysField = document.querySelector(PDQ.countdown.days);
        this.countdownHoursField = document.querySelector(PDQ.countdown.hours);

        this.countdownMinutesField = document.querySelector(
            PDQ.countdown.minutes,
        );

        this.countdownSecondsField = document.querySelector(
            PDQ.countdown.seconds,
        );

        if (!modal || !swiperWrapper) {
            return;
        }

        this.initialDays = getParsedValue(this.countdownDaysField);
        this.initialHours = getParsedValue(this.countdownHoursField);
        this.initialMinutes = getParsedValue(this.countdownMinutesField);
        this.initialSeconds = getParsedValue(this.countdownSecondsField);

        this.totalSecondsConfig = {
            days: this.initialDays,
            hours: this.initialHours,
            minutes: this.initialMinutes,
            seconds: this.initialSeconds,
        };

        this.countdownTimer = new CountdownTimer(this.totalSecondsConfig, {
            days: this.countdownDaysField,
            hours: this.countdownHoursField,
            minutes: this.countdownMinutesField,
            seconds: this.countdownSecondsField,
        });

        this.countdownTimer.start();

        modalButton.addEventListener("click", async () => {
            await tryCatch(
                async () =>
                    await this.initPDQSlider(
                        modalId,
                        swiperWrapper,
                        hasIntroductoryImage,
                    ),
            )();

            setTimeout(
                () => activateClasses(PDQ.modalId, [".left", ".right"]),
                300,
            );

            setTimeout(
                () => activateClasses(PDQ.modalId, [".down", ".up"]),
                800,
            );
        });
    }

    async initPDQSlider(modalId, swiperWrapper, hasIntroductoryImage) {
        if (!this.pdqSliderHorizontal) {
            this.pdqSliderHorizontal = new swiperScroll(
                this.swiperIdHorizontal,
                coverflowSwiperOptions,
            );

            const swiper = this.pdqSliderHorizontal._swiper;

            const [firstEl, secondEl] = [
                swiperWrapper.querySelector(PDQ.firstIntroductorySlide),
                swiperWrapper.querySelector(PDQ.secondIntroductorySlide),
            ];

            const handleSlideChange = () => {
                let indexThreshold = hasIntroductoryImage ? 2 : 1;
                const isFirstTwo = swiper.activeIndex < indexThreshold;

                modalId.classList.toggle(PDQ.introductorySlide, isFirstTwo);

                [firstEl, secondEl].forEach((el) =>
                    el.classList.toggle(
                        PDQ.introductorySlideOverlay,
                        !isFirstTwo,
                    ),
                );
            };

            swiper.on("slideChange reachBeginning", handleSlideChange);
        } else {
            this.pdqSliderHorizontal.updateTranslate(this.translate);
        }

        if (!this.pdqSliderVertical) {
            const self = this;

            this.pdqSliderVertical = this.swiperIdVertical.map((id) => {
                const verticalSlider = new swiperScroll(
                    id,
                    alternateVerticalSwiperOptions,
                );

                verticalSlider._swiper.swipeTriggered = {
                    up: false,
                    down: false,
                };

                verticalSlider._swiper.on("setTranslate", function () {
                    const activeOuterSlide = this.el.closest(PDQ.slider.outer);
                    const thresholdUp = PDQ.values.TRANSLATE_THRESHOLD_UP;
                    const thresholdDown = PDQ.values.TRANSLATE_THRESHOLD_DOWN;

                    if (
                        this.translate < thresholdUp &&
                        !this.swipeTriggered.up
                    ) {
                        if (!activeOuterSlide) return;

                        self.handleAction(this, PDQ.arrow.up, activeOuterSlide);
                        this.swipeTriggered.up = true;
                    } else if (this.translate >= thresholdUp) {
                        this.swipeTriggered.up = false;
                    }

                    if (
                        this.translate > thresholdDown &&
                        !this.swipeTriggered.down
                    ) {
                        if (!activeOuterSlide) return;

                        self.handleAction(
                            this,
                            PDQ.arrow.down,
                            activeOuterSlide,
                        );

                        this.swipeTriggered.down = true;
                    } else if (this.translate <= thresholdDown) {
                        this.swipeTriggered.down = false;
                    }
                });

                const sliderElement = document.getElementById(id);

                if (sliderElement) {
                    sliderElement.addEventListener("click", (e) => {
                        const arrow = e.target.closest(PDQ.arrow.base);

                        if (!arrow) return;

                        e.preventDefault();
                        const outerSlide = arrow.closest(PDQ.slider.outer);

                        if (arrow.matches(PDQ.arrow.up)) {
                            if (
                                outerSlide &&
                                outerSlide.matches(PDQ.activeOuterSlide)
                            ) {
                                this.handleAction(
                                    verticalSlider._swiper,
                                    PDQ.arrow.up,
                                    outerSlide,
                                );
                            }
                        } else if (arrow.matches(PDQ.arrow.down)) {
                            this.handleAction(
                                verticalSlider._swiper,
                                PDQ.arrow.down,
                                outerSlide,
                            );
                        }
                    });
                }

                return verticalSlider;
            });
        }
    }

    async handleAction(
        swiperInstance,
        selector,
        outerSlide = null,
        delay = PDQ.values.ACTION_DELAY,
    ) {
        const activeIndex = this.pdqSliderHorizontal._swiper.activeIndex;

        if (
            !outerSlide &&
            (selector === PDQ.arrow.down || selector === PDQ.arrow.up)
        )
            return;

        const productId = this.getProductIdFromSlide(outerSlide);

        if (selector === PDQ.arrow.down) {
            const { success } = await this.api.fetchAddProduct(productId);

            if (success) {
                outerSlide.classList.add("bought");
                basket.updateCartAmount(1);
            }
        } else if (selector === PDQ.arrow.up) {
            const { success } = await this.api.fetchRemoveProduct(productId);
            if (success) outerSlide.classList.add("removed");
        }

        setTimeout(() => {
            swiperInstance.destroy();
            this.pdqSliderHorizontal._swiper.removeSlide(activeIndex);
        }, delay);
    }

    getProductIdFromSlide(slide) {
        const itemWrap = slide.querySelector(PDQ.itemWrap);
        return itemWrap?.getAttribute(PDQ.dataProdId)?.match(/\d+/)[0];
    }
}
